// benchMerge.js — one card per carton on the Auto Imposer bench (#87 follow-up).
//
// A job keys every file by its source_id (the file name without its extension),
// and two rows with one source_id are refused at layout time
// (DuplicateCartonName). So adding a carton that is already on the bench must
// not make a second card:
//
//   the same file, under any name → the card already there gets the copies
//   another file, same source_id  → refused: one of the two needs another name
//   otherwise                     → a new card
//
// "Same file" means equal SHA-256 digests of the bytes, or the same library id.
// It is checked FIRST: a carton restored from the library carries the name it
// was saved under ("… copy.pdf"), so the same bytes can arrive named otherwise.
// The Editor desk already refuses a second row for one source_id; this is the
// bench's version of the same rule, keeping the copies instead of dropping them.
// Pure (fileDigest only needs Web Crypto, which node 20 has too), mirrored to
// .cjs and tested by src/benchmerge.test.js.

import { sourceIdOf } from './prepress';

/** SHA-256 of a File/Blob as hex, or null when it cannot be read. */
export async function fileDigest(file) {
  try {
    const buf = await file.arrayBuffer();
    const hash = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

function sameFile(a, b) {
  return !!((a.digest && b.digest && a.digest === b.digest)
    || (a.libraryId && b.libraryId && a.libraryId === b.libraryId));
}

/**
 * Fold `incoming` cartons into `bench`. Items carry { id, name, count, digest,
 * libraryId, ... }. Returns the new bench and what happened to each incoming
 * carton: `added` (new cards), `merged` ({ name, count } — copies added to a
 * card already there), `refused` ({ name } — another file has that name).
 */
export function mergeIntoBench(bench, incoming) {
  const rows = (bench || []).map((c) => ({ ...c }));
  const added = [];
  const merged = [];
  const refused = [];
  (incoming || []).forEach((c) => {
    const twin = rows.findIndex((r) => sameFile(r, c));
    if (twin >= 0) {
      rows[twin] = { ...rows[twin], count: Math.max(1, rows[twin].count || 1) + Math.max(1, c.count || 1) };
      merged.push({ name: c.name, onBenchAs: rows[twin].name, count: rows[twin].count });
      return;
    }
    const sid = sourceIdOf(c);
    if (rows.some((r) => sourceIdOf(r) === sid)) {
      refused.push({ name: c.name });
    } else {
      rows.push({ ...c });
      added.push(c);
    }
  });
  return { bench: rows, added, merged, refused };
}

/** The toast after an add: what was added, merged and refused, in words. */
export function benchAddSummary({ added, merged, refused }) {
  const parts = [];
  if (added.length) parts.push(`Added: ${added.map((c) => c.name).join(', ')}.`);
  merged.forEach((m) => parts.push(m.onBenchAs && m.onBenchAs !== m.name
    ? `${m.name} is the same file as ${m.onBenchAs}, already on the bench — copies now ${m.count}.`
    : `${m.name} was already on the bench — copies now ${m.count}.`));
  refused.forEach((r) => parts.push(`${r.name} not added: a different file with the same name is already on the bench. Rename one of them.`));
  const n = added.length;
  return {
    kind: refused.length ? 'warn' : 'success',
    title: n ? `${n} carton${n > 1 ? 's' : ''} added` : (merged.length ? 'Copies added' : 'Nothing added'),
    message: parts.join(' '),
  };
}
